import type { FirebaseClient } from '@akpl/firebase/client';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { z } from 'zod';

/**
 * `/sessions/{sid}/notices/{id}` (docs/13 §2), written by the API when a guest asks for a song the
 * host's plan does not cover. Local rather than in `@akpl/shared` until the backend lands the node.
 */
export const SessionNotice = z.object({
  type: z.literal('premium_blocked'),
  name: z.string(),
  title: z.string(),
  at: z.number(),
});
export type SessionNotice = z.infer<typeof SessionNotice>;

export const NoticesNode = z.record(z.string(), SessionNotice);

export const noticesPath = (sid: string) => `/sessions/${sid}/notices`;

const seenKey = (sid: string) => `akp.web.notices.${sid}`;

function readSeen(sid: string): string[] {
  try {
    const raw = localStorage.getItem(seenKey(sid));
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((id): id is string => typeof id === 'string') : [];
  } catch {
    return [];
  }
}

export interface PendingNotice extends SessionNotice {
  id: string;
}

export function pendingNotices(
  node: Record<string, SessionNotice> | null,
  seen: readonly string[],
): PendingNotice[] {
  return Object.entries(node ?? {})
    .filter(([id]) => !seen.includes(id))
    .map(([id, notice]) => ({ ...notice, id }))
    .sort((a, b) => a.at - b.at);
}

/** E-22. Seen ids outlive a reload, so the same notice is not repeated (docs/13 §5.5). */
export function useNotices(sid: string | null, client: FirebaseClient | null) {
  const [node, setNode] = useState<Record<string, SessionNotice> | null>(null);
  const [seenNow, setSeenNow] = useState<string[]>([]);
  const stored = useMemo(() => (sid ? readSeen(sid) : []), [sid]);

  useEffect(() => {
    if (!sid || !client) return undefined;
    const off = client.listen(noticesPath(sid), NoticesNode, setNode);
    return () => {
      off();
      setNode(null);
    };
  }, [sid, client]);

  const markSeen = useCallback(
    (id: string) => {
      setSeenNow((current) => {
        const next = [...current, id];
        if (sid) {
          try {
            localStorage.setItem(seenKey(sid), JSON.stringify([...stored, ...next]));
          } catch {
            /* private mode: seen for this page load only */
          }
        }
        return next;
      });
    },
    [sid, stored],
  );

  const notices = useMemo(
    () => pendingNotices(node, [...stored, ...seenNow]),
    [node, seenNow, stored],
  );
  return { notices, markSeen };
}
